'use client';

import { useEffect, useRef, useState } from 'react';

import { useAtom, useSetAtom } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';
import { maxMonthlyPriceAtom, maxTotalPriceAtom } from '@search/Atoms/MaxPriceAtom';
import type { PriceItemAtom } from '@search/Atoms/PriceItemAtom.entity';
import type { PriceTypeAtom } from '@search-filters/Atom/PriceTypeAtom';
import { PRICE_OPTIONS } from '@shared-components/SearchWidget/Utils/PriceUtils';

import type { searchWidgetMessages } from '../../SearchWidget.messages';
import CustomDropDown from '../CustomDropDown/CustomDropDown';
import type { CustomDropDownProps } from '../CustomDropDown/CustomDropDown.entity';

import styles from '../../Search.module.css';

type MaxPriceProps = {
  priceFilterType: PriceTypeAtom;
  strings: Record<keyof typeof searchWidgetMessages, string>;
};

export const MaxPrice = ({ priceFilterType, strings }: MaxPriceProps) => {
  const DEFAULT_OPTION = {
    key: 'any_price',
    displayName: strings.defaultPriceLabel,
    count: 0,
  };
  const [maxPrice, setMaxPrice] = useAtom(
    priceFilterType === 'monthly-price' ? maxMonthlyPriceAtom : maxTotalPriceAtom,
  );
  const setFilters = useSetAtom(filtersListAtom);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const placeholder =
    priceFilterType === 'monthly-price' ? strings.maxMonthlyPriceLabel : strings.maxTotalPriceLabel;
  const filterId = (
    priceFilterType === 'monthly-price' ? 'monthly-price__lte' : 'price__lte'
  ) as FilterKey;

  const correctPriceOptions: PriceItemAtom[] = PRICE_OPTIONS[priceFilterType].map(o => ({
    key: String(o.value),
    displayName: o.label,
    count: 0,
  }));
  const allItems = [DEFAULT_OPTION, ...correctPriceOptions];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect: NonNullable<CustomDropDownProps['onSelect']> = option => {
    const value = (option as PriceItemAtom).key;
    setMaxPrice(value);
    setFilters(prev =>
      value
        ? [...prev.filter(f => f.id !== filterId), { id: filterId, value }]
        : prev.filter(f => f.id !== filterId),
    );
    setIsOpen(false);
  };

  const selected = allItems.find(i => i.key === maxPrice);

  return (
    <div
      className={`${styles.makeModelWrapper} ${isOpen ? styles.active : ''}`}
      ref={ref}
      data-test-id="max-price-dropdown"
    >
      <button
        type="button"
        className={styles.dropdownWrapper}
        onClick={() => setIsOpen(o => !o)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={styles.makeValue}>{selected?.displayName ?? placeholder}</span>
        <div className={styles.iconWrapper}>
          <ChevronDown className={isOpen ? styles.rotate : ''} />
        </div>
      </button>
      {isOpen && (
        <CustomDropDown
          items={allItems}
          onSelect={handleSelect}
          checkedValue={maxPrice}
          dataTestId="max-price-menu"
          title={placeholder}
          handleMenuOpen={() => setIsOpen(false)}
          dropdownType="price"
          presorted
        />
      )}
    </div>
  );
};
